import {
  EntityModel,
  FieldDefinition,
  FormEntityViewDefinition,
  FormFieldDefinition,
  FormFormDefinition,
  FormGroupDefinition,
  FormModel,
  FormViewDefinition,
  MessageModel,
  UIElementDefinition,
} from '../definitions'
import { EntityContext } from '../entity-context'
import { getNameAndAlias, readEntityModelByName, readMessageModelByCode } from '../utils'
import { getControl, getFormControl, getViewControl } from './control-builder'
import { FormNode } from './form-control'

/**
 * The `formTraverse` function recursively traverses a tree-like structure of form nodes and calls a
 * callback function on each node.
 * @param {FormNode | undefined} node - The `node` parameter is of type `FormNode` or `undefined`. It
 * represents a node in a form structure. If it is `undefined`, the function will return without doing
 * anything.
 * @param callback - The `callback` parameter is a function that will be called for each `FormNode` in
 * the traversal. It takes a single argument, which is the current `FormNode` being traversed.
 * @returns nothing (undefined) if the `node` parameter is falsy (undefined).
 */
export function formTraverse(node: FormNode | undefined, callback: (node: FormNode) => void) {
  if (!node) {
    return
  }

  callback(node)

  formTraverse(node.firstChild, callback)
  formTraverse(node.nextSibling, callback)
}

/**
 * The `traverseSiblings` function recursively traverses through the siblings of a given node and
 * applies a callback function to each sibling.
 * @param {FormNode | undefined} node - The `node` parameter is of type `FormNode` or `undefined`. It
 * represents the current node that we want to traverse its siblings.
 * @param callback - The `callback` parameter is a function that will be called for each sibling node
 * in the traversal. It takes a single parameter `node` which represents the current sibling node being
 * traversed.
 * @returns nothing (undefined).
 */
export function traverseSiblings(node: FormNode | undefined, callback: (node: FormNode) => void) {
  if (!node) {
    return
  }

  callback(node)

  traverseSiblings(node.nextSibling, callback)
}

/**
 * The function `findNode` recursively searches for a node with a specific name within a tree-like
 * structure.
 * @param {FormNode | undefined} node - The `node` parameter is of type `FormNode` or `undefined`. It
 * represents the current node being searched.
 * @param {string} nodeName - The `nodeName` parameter is a string that represents the name of the node
 * you are searching for.
 * @returns a `FormNode` object if a node with the specified `nodeName` is found. If no node is found,
 * it returns `null`.
 */
export function findNode(node: FormNode | undefined, nodeName: string): FormNode | null {
  if (!node) {
    return null
  }

  if (node.id === nodeName) {
    return node
  }

  let foundNode = findNode(node.firstChild, nodeName)
  if (foundNode) {
    return foundNode
  }

  foundNode = findNode(node.nextSibling, nodeName)
  return foundNode
}

/**
 * The function `formCreateControls` creates a form node hierarchy based on a list of UI element
 * definitions, an entity model, a form model, and an optional entity context.
 * @param {UIElementDefinition[]} items - An array of UIElementDefinition objects that define the
 * structure and layout of the form controls.
 * @param {EntityModel} entityModel - The `entityModel` parameter is an object that represents the
 * model of the entity being used in the form. It contains information about the fields, validations,
 * and other properties of the entity.
 * @param {FormModel} formModel - The `formModel` parameter is an object that represents the current
 * state of the form. It contains information such as the values of the form fields, their validation
 * state, and any error messages associated with them.
 * @param {EntityContext} [entity] - The `entity` parameter is an optional parameter of type
 * `EntityContext`. It represents the current entity being used in the form.
 * @returns the latest FormNode that was created.
 */
export function formCreateControls(
  items: UIElementDefinition[],
  entityModel: EntityModel,
  formModel: FormModel,
  rootFormModelName: string,
  entity?: EntityContext,
): FormNode {
  let prevChildren: FormNode
  let latest: FormNode | null = null
  const reversed = [...items].reverse()
  reversed.forEach((item) => {
    let control: FormNode | null = null
    if (item.type === 'Group' || item.type === 'Tab') {
      const groupDef = item as FormGroupDefinition
      const lastChild = groupDef.layout
        ? formCreateControls(groupDef.layout, entityModel, formModel, rootFormModelName, entity)
        : undefined
      control = {
        id: (item as FormGroupDefinition).name,
        firstChild: lastChild,
        nextSibling: prevChildren,
        value: {
          data: null,
          type: item.type,
          state: 'Enabled',
          visibility: 'Visible',
          validationMessages: [],
          validationParams: [],
          validationState: 'Valid',
          formDefinition: item,
          isDirty: false,
          validationFlags: 0,
          entityName: entity?.entityName as string,
          rootFormModelName,
        },
      }
    } else if (item.type === 'CustomTab') {
      control = {
        id: (item as FormGroupDefinition).name,
        nextSibling: prevChildren,
        value: {
          data: null,
          type: item.type,
          state: 'Enabled',
          visibility: 'Visible',
          validationMessages: [],
          validationParams: [],
          validationState: 'Valid',
          formDefinition: item,
          isDirty: false,
          validationFlags: 0,
          entityName: entity?.entityName as string,
          rootFormModelName,
        },
      }
    } else if (item.type === 'Field' || item.type === 'MultiSelector' || item.type === 'MetadataSelector') {
      const casted = item as FormFieldDefinition
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      const fd = getFieldDefinition(casted.name, entityModel, formModel)
      if (!fd) return
      control = getControl(
        {
          fieldDef: fd,
          formDef: casted,
          entity,
          prevChildren,
        },
        getEntityModelForFormField(casted.name, entityModel, formModel),
        formModel,
        rootFormModelName,
      )
    } else if (item.type === 'EntityView') {
      const entityViewDef = item as FormEntityViewDefinition
      control = {
        id: `EntityView_${entityViewDef.entityName}`,
        nextSibling: prevChildren,
        value: {
          data: null,
          type: 'EntityView',
          state: 'Enabled',
          visibility: 'Visible',
          validationMessages: [],
          validationParams: [],
          validationState: 'Valid',
          formDefinition: item,
          isDirty: false,
          validationFlags: 0,
          entityName: entityViewDef.entityName,
          rootFormModelName,
        },
      }
    } else if (item.type === 'Form') {
      const casted = item as FormFormDefinition
      control = getFormControl(
        {
          formDef: casted,
          entity,
          prevChildren,
        },
        rootFormModelName,
      )
    } else if (item.type === 'View') {
      const casted = item as FormViewDefinition
      control = getViewControl(
        {
          formDef: casted,
          entity,
          prevChildren,
        },
        rootFormModelName,
      )
    }

    if (control) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      prevChildren = control as any
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      latest = control as any
      control
    }
  })
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return latest as any
}

/**
 * The function `isTreeValid` checks if a tree of form nodes is valid by traversing the tree and
 * checking if any nodes have validation messages.
 * @param {FormNode} root - The `root` parameter is the root node of a tree-like data structure. In
 * this case, it represents a form node.
 * @returns a boolean value, indicating whether the tree represented by the root node is valid or not.
 */
export function isTreeValid(root: FormNode): boolean {
  let isValid = true
  formTraverse(root, (node) => {
    if (node.value.validationMessages.length) {
      node.value.validationMessages.forEach((m) => {
        const model = readMessageModelByCode(m) as MessageModel
        isValid = isValid && model.type >= 30
      })
    }
  })
  return isValid
}

/**
 * all the tree will have the state set to `Readonly`
 */
export function disableTree(root: FormNode): void {
  formTraverse(root, (node) => {
    node.value.state = 'Readonly'
  })
}

/**
 * The function getFieldDefinition retrieves the field definition for a given field name from either an
 * entity model or a form model.
 * @param {string} fieldName - The `fieldName` parameter is a string that represents the name of a
 * field in an entity or form. It can also include a dot notation to specify a nested field within an
 * entity.
 * @param {EntityModel} entityModel - The `entityModel` parameter is an object that represents the
 * model of an entity. It contains information about the fields and properties of the entity, such as
 * their names, types, and validation rules.
 * @param {FormModel} formModel - The `formModel` parameter is an object that represents the model of a
 * form. It contains information about the fields in the form, such as their names, types, and
 * validation rules.
 * @returns a FieldDefinition object.
 */
function getFieldDefinition(fieldName: string, entityModel: EntityModel, formModel: FormModel): FieldDefinition {
  if (fieldName.includes('.')) {
    const resolved = getNameAndAlias(fieldName)
    if (resolved.alias === 'FE') {
      return formModel.fields.find((f) => f.name === resolved.name) as FieldDefinition
    } else {
      const link = (formModel.linkEntities || []).find((lk) => lk.as === resolved.alias)
      if (!link) {
        throw new Error(`Error while building the form, there is an alias on a field
          but no matching link could be found, alias: ${resolved.alias}`)
      }
      const model = readEntityModelByName(link?.entity as string)
      return model?.getFieldDefinition(resolved.name) as FieldDefinition
    }
  } else {
    return entityModel.getFieldDefinition(fieldName)
  }
}

/**
 * In case of linked entities inside the form sometimes the field could come from another entity
 * this metod resolves for the given fieldName the correct entity model to use
 */
export function getEntityModelForFormField(
  fieldName: string,
  entityModel: EntityModel,
  formModel: FormModel,
): EntityModel {
  if (fieldName.includes('.')) {
    const resolved = getNameAndAlias(fieldName)
    if (resolved.alias === 'FE') {
      return entityModel
    } else {
      const link = (formModel.linkEntities || []).find((lk) => lk.as === resolved.alias)
      if (!link) {
        throw new Error(`Error while building the form, there is an alias on a field
          but no matching link could be found, alias: ${resolved.alias}`)
      }
      const model = readEntityModelByName(link?.entity as string) as EntityModel
      return model
    }
  } else {
    return entityModel
  }
}
